import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { imageService } from '../services/ImageService';

const ImageThumbnailStack = ({ images, uid }) => {
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchUrls = async () => {
      if (!images || images.length === 0) {
        if (isMounted) {
          setImageUrls([]);
          setLoading(false);
        }
        return;
      }

      // Only fetch first 3 images for the stack
      const imagesToFetch = images.slice(0, 3);
      
      try {
        const urls = await Promise.all(
          imagesToFetch.map(imageUID => imageService.getImageURL(uid, imageUID, true))
        );
        
        if (isMounted) {
          setImageUrls(urls.filter(url => url !== null));
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching thumbnails:", error);
        if (isMounted) setLoading(false);
      }
    };

    fetchUrls();

    return () => {
      isMounted = false;
    };
  }, [images, uid]);

  if (!images || images.length === 0) return null;

  if (loading) {
    // Simple skeleton placeholder
    return (
      <div className="w-[50px] h-[50px] bg-gray-200 rounded-lg animate-pulse border-2 border-gray-100"></div>
    );
  }

  // If URLs failed to load, don't render anything or render error placeholder
  if (imageUrls.length === 0) return null;

  const extraCount = images.length > 3 ? images.length - 3 : 0;

  // Helper to get style for index
  const getStyle = (index) => {
    // Base styles
    const base = {
      width: '50px',
      height: '50px',
      borderRadius: '8px',
      border: '2px solid rgba(255, 255, 255, 0.8)', // White border looks better on stack
      objectFit: 'cover',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      position: 'absolute',
      transition: 'transform 0.2s ease',
    };

    // Stack effect logic from spec
    if (images.length === 1) {
      return { ...base, transform: 'rotate(0deg)', zIndex: 1 };
    }

    switch (index) {
      case 0: // Bottom
        return { 
          ...base, 
          transform: 'rotate(-5deg)', 
          zIndex: 1,
          left: 0, 
          top: 0 
        };
      case 1: // Middle
        return { 
          ...base, 
          transform: 'rotate(5deg) translate(4px, -4px)', 
          zIndex: 2,
          left: 0,
          top: 0
        };
      case 2: // Top
        return { 
          ...base, 
          transform: 'rotate(15deg) translate(8px, -8px)', 
          zIndex: 3,
          left: 0,
          top: 0
        };
      default:
        return base;
    }
  };

  // Container width calculation: 50px + offset
  // For 1 image: 50px
  // For 2 images: ~54px
  // For 3 images: ~58px
  const containerWidth = 50 + (Math.min(imageUrls.length, 3) - 1) * 4;
  const containerHeight = 50; // Base height, though rotation adds a bit

  return (
    <div 
      className="relative inline-block ml-2 flex-shrink-0" 
      style={{ width: `${containerWidth}px`, height: `${containerHeight}px` }}
    >
      {imageUrls.map((url, index) => (
        <img
          key={`${images[index]}-${index}`}
          src={url}
          alt={`Thumbnail ${index + 1}`}
          style={getStyle(index)}
          className="bg-gray-100"
        />
      ))}

      {extraCount > 0 && (
        <div
          className="absolute flex items-center justify-center bg-white/90 backdrop-blur-sm px-1.5 py-0.5 rounded-full shadow-sm border border-gray-200"
          style={{
            zIndex: 4,
            top: '-8px',
            right: '-12px', // Adjusted to approximate spec "Offset (15px, -15px) from 3rd image" relative to container
            transform: 'translate(0, 0)',
          }}
        >
          <span className="text-[10px] font-bold text-gray-700">+{extraCount}</span>
        </div>
      )}
    </div>
  );
};

ImageThumbnailStack.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string),
  uid: PropTypes.string.isRequired,
};

export default ImageThumbnailStack;

